import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, ArrowLeft, Zap
} from 'lucide-react';

// YAS Design V1: "Fresh Start"
// Clean Daydream layout + YAS teal/orange + soft cream accents
// Playful: Tilted cards, blob shapes, subtle patterns

const YAS_LOGO = '/yas-logo.png';

const YASDesignV1 = () => {
  const navigate = useNavigate();

  // YAS Brand Colors + Complementary
  const colors = {
    teal: '#1B2D4F',
    tealLight: '#2A8A9D',
    tealDark: '#145566',
    orange: '#E86C3D',
    orangeLight: '#FF8A5C',
    cream: '#FFF8F0',
    warmWhite: '#FFFCF9',
    coral: '#FF7F6B',
    mint: '#B8E0D2',
  };

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: colors.cream,
      fontFamily: "'Quicksand', sans-serif"
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white rounded-2xl p-2.5 shadow-lg hover:shadow-xl transition-shadow"
      >
        <ArrowLeft className="w-5 h-5" style={{ color: colors.teal }} />
      </button>

      {/* Theme Label */}
      <div 
        className="fixed top-4 right-4 z-50 text-white px-4 py-2 rounded-2xl font-bold text-sm shadow-lg"
        style={{ backgroundColor: colors.teal }}
      >
        YAS V1: Fresh Start
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden">
        {/* Gradient background */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 50%, ${colors.tealDark} 100%)`
          }} 
        />
        
        {/* Playful blob shapes */}
        <div 
          className="absolute top-[-50px] right-[-30px] w-40 h-40 rounded-full blur-xl"
          style={{ backgroundColor: colors.orange, opacity: 0.3 }}
        />
        <div 
          className="absolute top-20 left-[-40px] w-32 h-32 rounded-full blur-lg"
          style={{ backgroundColor: colors.mint, opacity: 0.4 }}
        />
        <div 
          className="absolute bottom-10 right-10 w-20 h-20 rounded-full"
          style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
        />
        
        {/* Diagonal stripe accent */}
        <div 
          className="absolute top-0 right-0 w-32 h-full opacity-10"
          style={{ 
            background: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${colors.orange} 10px, ${colors.orange} 20px)`
          }}
        />
        
        <div className="relative px-4 pt-16 pb-10">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg overflow-hidden">
                  <img src={YAS_LOGO} alt="YAS" className="w-12 h-12 object-contain" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-semibold opacity-80">Youth 2 Adult Success</p>
                  <p className="text-sm font-bold">Summer 2026</p>
                </div>
              </div>
              <button className="bg-white/20 backdrop-blur rounded-xl p-3 hover:bg-white/30 transition-colors">
                <LogOut className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Welcome Section */}
            <div className="text-center mb-8">
              <div 
                className="w-24 h-24 mx-auto rounded-3xl bg-white flex items-center justify-center mb-4 shadow-xl"
                style={{ transform: 'rotate(-3deg)' }}
              >
                <span className="text-4xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>B</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                What's good, Brandon!
              </h1>
              <p className="text-white/80 text-sm font-medium">Let's get this bread today</p>
              <div className="flex justify-center gap-2 mt-3">
                <span 
                  className="text-white text-xs font-bold px-4 py-1.5 rounded-full"
                  style={{ backgroundColor: colors.orange }}
                >
                  UpNEXT SYEP
                </span>
              </div>
            </div>

            {/* Worksite Info - Tilted card */}
            <div 
              className="bg-white/20 backdrop-blur rounded-2xl p-4 mb-6"
              style={{ transform: 'rotate(1deg)' }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.orange }}
                >
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-semibold opacity-70">Your Spot</p>
                  <p className="font-bold">Newark Public Library</p>
                </div>
              </div>
            </div>

            {/* Stats Grid - Alternating tilts */}
            <div className="grid grid-cols-2 gap-4">
              {/* Points Card */}
              <div 
                className="bg-white rounded-2xl p-5 shadow-lg"
                style={{ transform: 'rotate(-2deg)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${colors.orange}20` }}
                  >
                    <Star className="w-4 h-4" style={{ color: colors.orange }} fill="currentColor" />
                  </div>
                  <span className="text-xs font-bold uppercase" style={{ color: colors.teal }}>Points</span>
                </div>
                <p className="text-3xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>
                  1,250
                </p>
              </div>

              {/* Streak Card */}
              <div 
                className="bg-white rounded-2xl p-5 shadow-lg"
                style={{ transform: 'rotate(2deg)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${colors.coral}20` }}
                  >
                    <Flame className="w-4 h-4" style={{ color: colors.coral }} />
                  </div>
                  <span className="text-xs font-bold uppercase" style={{ color: colors.teal }}>Streak</span>
                </div>
                <p className="text-3xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>
                  7 <span className="text-lg font-normal" style={{ color: colors.tealLight }}>days</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave bottom */}
        <svg className="absolute bottom-0 left-0 right-0" viewBox="0 0 1440 60" fill="none">
          <path d="M0 60V30C240 10 480 0 720 10C960 20 1200 40 1440 30V60H0Z" fill={colors.cream} />
        </svg>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-2 space-y-6">
        
        {/* Quick Actions - Horizontal with playful cards */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { icon: Target, label: 'Goals', rotate: '-2deg' },
            { icon: Briefcase, label: 'Project', rotate: '1deg' },
            { icon: Trophy, label: 'Rankings', rotate: '-1deg' },
            { icon: Award, label: 'Wins', rotate: '2deg' },
            { icon: FolderOpen, label: 'Files', rotate: '-1deg' },
            { icon: HelpCircle, label: 'Help', rotate: '1deg' },
          ].map((item, idx) => (
            <button
              key={item.label}
              className="flex-shrink-0 bg-white rounded-2xl p-4 min-w-[85px] shadow-md hover:shadow-lg transition-all hover:scale-105"
              style={{ transform: `rotate(${item.rotate})` }}
            >
              <div 
                className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center"
                style={{ backgroundColor: idx % 2 === 0 ? `${colors.teal}15` : `${colors.orange}15` }}
              >
                <item.icon className="w-5 h-5" style={{ color: idx % 2 === 0 ? colors.teal : colors.orange }} />
              </div>
              <p className="text-xs font-bold text-center" style={{ color: colors.teal }}>{item.label}</p>
            </button>
          ))}
        </div>

        {/* Work Journal Card - Main CTA */}
        <div 
          className="rounded-3xl p-6 relative overflow-hidden shadow-xl"
          style={{ backgroundColor: colors.orange, transform: 'rotate(-1deg)' }}
        >
          {/* Decorative circles */}
          <div className="absolute top-[-20px] right-[-20px] w-24 h-24 rounded-full bg-white/20" />
          <div className="absolute bottom-[-15px] left-10 w-16 h-16 rounded-full bg-white/10" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-md">
                <Sparkles className="w-6 h-6" style={{ color: colors.orange }} />
              </div>
              <div className="text-white">
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  Work Journal
                </h2>
                <p className="text-white/80 text-sm font-medium">Drop your weekly highlights</p>
              </div>
            </div>
            <button 
              className="w-full bg-white rounded-2xl py-4 font-bold text-lg hover:bg-gray-50 transition-all shadow-md"
              style={{ color: colors.orange, fontFamily: "'Fredoka', sans-serif" }}
            >
              Drop a Win! +50 pts
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div 
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${colors.teal}15` }}
            >
              <Calendar className="w-4 h-4" style={{ color: colors.teal }} />
            </div>
            <h2 className="text-lg font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>
              This Week
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div 
              className="rounded-2xl p-5 cursor-pointer shadow-lg hover:shadow-xl transition-shadow"
              style={{ 
                background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
                transform: 'rotate(0.5deg)'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Skills Check</h3>
                    <p className="text-white/80 text-sm">Level up • 50 pts</p>
                  </div>
                </div>
                <span 
                  className="text-white text-xs font-bold px-3 py-1.5 rounded-full"
                  style={{ backgroundColor: colors.orange }}
                >
                  New
                </span>
              </div>
            </div>

            {/* Check-in Card */}
            <div 
              className="bg-white rounded-2xl p-4 cursor-pointer shadow-md hover:shadow-lg transition-shadow"
              style={{ transform: 'rotate(-0.5deg)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${colors.mint}` }}
                  >
                    <CheckCircle className="w-5 h-5" style={{ color: colors.teal }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>Weekly Check-in</h3>
                    <p className="text-sm" style={{ color: colors.tealLight }}>How's your week? • 20 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.tealLight }} />
              </div>
            </div>

            {/* Trivia Card */}
            <div 
              className="bg-white rounded-2xl p-4 cursor-pointer shadow-md hover:shadow-lg transition-shadow"
              style={{ transform: 'rotate(0.5deg)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${colors.orange}20` }}
                  >
                    <Zap className="w-5 h-5" style={{ color: colors.orange }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>Weekly Trivia</h3>
                    <p className="text-sm" style={{ color: colors.tealLight }}>Test yourself • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.tealLight }} />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div 
          className="bg-white rounded-3xl p-5 relative overflow-hidden shadow-lg"
          style={{ transform: 'rotate(-0.5deg)' }}
        >
          <div 
            className="absolute top-0 right-0 w-20 h-20 rounded-full blur-xl"
            style={{ backgroundColor: colors.mint, opacity: 0.5 }}
          />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: colors.teal }}
                >
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>Team Alpha</h3>
                  <p className="text-sm" style={{ color: colors.tealLight }}>8 members</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>4,500</p>
                <p className="text-xs" style={{ color: colors.tealLight }}>team pts</p>
              </div>
            </div>
            <button 
              className="w-full rounded-xl py-3 font-bold transition-colors"
              style={{ 
                backgroundColor: `${colors.teal}15`, 
                color: colors.teal,
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              See Your Squad <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t px-4 py-2 shadow-top"
        style={{ borderColor: `${colors.teal}20` }}
      >
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: Target, label: 'Home', active: true },
            { icon: Target, label: 'Goals', active: false },
            { icon: Trophy, label: 'Progress', active: false },
            { icon: Users, label: 'Profile', active: false },
          ].map((item) => (
            <button 
              key={item.label} 
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all"
              style={{ 
                backgroundColor: item.active ? `${colors.teal}15` : 'transparent',
                color: item.active ? colors.teal : colors.tealLight
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default YASDesignV1;
